import React, { useEffect, useState } from "react";

import { Button, MenuItem, Modal, Stack, StackProps, TextField, Typography } from "@mui/material";
import { AsyncContentMui, ProgressOverlay } from "components";
import { SimpleHeadMui } from "components/Simple-head/SimpleHeadMui";
import { IApiServiceError, useDealActions } from "store";
import pickFiles from "utils/pick-files";

import { Group, useGroupsForm } from "./GroupsFormContext";

interface Props {
  deals: Pick<Group, "deal_id" | "sub_deal_id"> | null;
  onClose: () => void;
}

export const ImportCommitments: React.FC<Props> = ({ deals, onClose }) => {
  const { getDealRoundsFromState, importCommitments } = useGroupsForm();
  const { getDealRoundsWithInvestorsAction, importCommitmentAction } = useDealActions();

  const [options, setOptions] = useState<{ name: string; id: number }[]>([]);
  const [roundId, setRoundId] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [getRoundsLoading, setGetRoundsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string[]> | null>(null);

  const getAndSetOptions = async (deals: Pick<Group, "deal_id" | "sub_deal_id">) => {
    try {
      setGetRoundsLoading(true);

      const roundsFromState = getDealRoundsFromState({ dealId: deals.deal_id, filterDeletedRounds: true })
        ?.filter((item) => typeof item.id === "number")
        .map(({ id, name }) => ({ id, name })) as { name: string; id: number }[] | undefined;

      if (roundsFromState) return setOptions(roundsFromState);

      const roundsFromApi = (await getDealRoundsWithInvestorsAction({ dealId: deals.deal_id })).map(
        ({ round_id, round_name }) => ({ id: round_id, name: round_name })
      );
      setOptions(roundsFromApi);
      if (roundsFromApi.length === 1) {
        setRoundId(String(roundsFromApi[0].id));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setGetRoundsLoading(false);
    }
  };

  useEffect(() => {
    if (deals) {
      getAndSetOptions(deals);
    } else {
      setOptions([]);
    }
  }, [deals]);

  const handleClose = () => {
    setRoundId(null);
    setErrors(null);
    onClose();
  };

  const handleSubmit = async () => {
    const round_id = Number(roundId);
    if (!round_id) return;

    try {
      setSubmitLoading(true);
      if (!deals) return;

      // const { deal_id } = deals;

      const fileList = await pickFiles();
      if (!fileList || !fileList[0]) return;

      // Import commitments and get data
      const importedData = await importCommitmentAction({
        round_id,
        file: fileList[0]
      }).catch((e) => {
        const parsedErrors: Record<string, string[]> = Object.entries((e as IApiServiceError).errors).reduce(
          (acc, [key, values]) => ({ ...acc, [key]: values.flat() }),
          {}
        );

        if (parsedErrors) {
          setErrors(parsedErrors);
        }
        throw e;
      });

      // Create group and add data
      importedData && (await importCommitments(importedData));
      handleClose();
    } catch (e) {
      console.log(e);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal open={Boolean(deals)} onClose={handleClose}>
      <Stack sx={containerStyle}>
        <SimpleHeadMui onIconClick={handleClose}>Import Commitments</SimpleHeadMui>
        {errors ? (
          <Stack sx={{ p: "20px", gap: "20px", overflow: "hidden" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: "#d93025", alignSelf: "center" }}>
              Validation Errors
            </Typography>
            <Stack sx={{ overflow: "auto", gap: "20px" }}>
              {Object.entries(errors).map(([key, value]) => {
                return (
                  <Stack key={key} sx={{ gap: "4px" }}>
                    <Typography>{key}</Typography>
                    {value.map((error, index) => (
                      <Typography key={error}>{`${index + 1}. ${error}`}</Typography>
                    ))}
                  </Stack>
                );
              })}
            </Stack>
            <Button variant="contained" onClick={handleClose} sx={{ alignSelf: "center" }}>
              Close
            </Button>
          </Stack>
        ) : (
          <ProgressOverlay
            inProgress={submitLoading}
            containerSX={{ display: "flex", flexDirection: "column", p: "20px", gap: "20px" }}
          >
            <Typography>
              This will change the investors of the {deals?.sub_deal_id ? "parent" : "current"} deal.
            </Typography>
            <AsyncContentMui isLoading={getRoundsLoading}>
              <TextField fullWidth onChange={(e) => setRoundId(e.target.value)} value={roundId} select label="Rounds">
                {options?.map((round) => (
                  <MenuItem value={round.id} key={round.id}>
                    {round.name}
                  </MenuItem>
                ))}
              </TextField>
            </AsyncContentMui>
            <Stack sx={{ gap: "10px", flexDirection: "row", justifyContent: "flex-end" }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" disabled={!roundId} onClick={handleSubmit}>
                Import
              </Button>
            </Stack>
          </ProgressOverlay>
        )}
      </Stack>
    </Modal>
  );
};

const containerStyle: StackProps["sx"] = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  overflow: "hidden",
  outline: "none",
  bgcolor: "white",
  minWidth: 500,
  maxWidth: 500,
  minHeight: 200,
  maxHeight: 500,
  // gap: "20px",
  borderRadius: "8px",
  boxShadow:
    "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
};
